import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Send, Trash2, Upload, Loader2, ScrollText, Bot, User, MessageSquare, X, Sparkles, Copy, Check, MessageCircle } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import { api, API_BASE } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import SamiMemoMode from "../components/SamiMemoMode";
import { log } from "../lib/log";
import { buildWhatsAppLink } from "../data/firm";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const formatBytes = (n) => `${(n / 1024).toFixed(1)} KB`;

/**
 * Build a WhatsApp link that includes the user's most recent question
 * from the Sami conversation so the firm receives the context without
 * having to ask the client "what's the matter?" again. ``assistantIdx``
 * is the index of the assistant reply being acted on; we walk back to
 * the immediately-preceding user message.
 */
const buildSamiWaLink = (messages, assistantIdx, isAr) => {
  let userQ = "";
  for (let i = assistantIdx - 1; i >= 0; i--) {
    if (messages[i]?.role === "user" && messages[i]?.content?.trim()) {
      userQ = messages[i].content.trim();
      break;
    }
  }
  const MAX = 600;
  if (userQ.length > MAX) userQ = userQ.slice(0, MAX).trimEnd() + "…";

  const text = isAr
    ? (userQ
        ? `مرحباً، استشرت "سامي" المستشار الذكي بخصوص:\n\n«${userQ}»\n\nوأرغب في حجز استشارة كاملة مع محامي.`
        : "مرحباً، استخدمت المستشار الذكي «سامي» وأرغب في تحويل الاستشارة إلى محامي.")
    : (userQ
        ? `Hello, I just consulted Sami (the AI assistant) about:\n\n"${userQ}"\n\nI'd like to book a full consultation with a lawyer.`
        : "Hello, I used the Sami AI assistant and want to convert the analysis into a consultation with a lawyer.");

  return buildWhatsAppLink(text);
};

// Stable per-tab session id so backend can group related turns.
const _sessionIdRef = (() => {
  if (typeof window === "undefined") return { current: "ssr" };
  let id = sessionStorage.getItem("sami_session_id");
  if (!id) {
    id = `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("sami_session_id", id);
  }
  return { current: id };
})();

export default function Consultant() {
  const { lang } = useI18n();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isAr = lang === "ar";
  const isAdmin = user?.role === "admin";

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [laws, setLaws] = useState([]);
  const [tier, setTier] = useState("public"); // "public" | "staff"
  const isStaffUser = user?.role === "staff" || user?.role === "admin";

  // history sheet (signed-in users only)
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // upload state (admin)
  const [upTitle, setUpTitle] = useState("");
  const [upLang, setUpLang] = useState("ar");
  const [upFile, setUpFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const scrollRef = useRef(null);
  // Holds a question that should be auto-sent once the chat is ready
  // (e.g. "Ask Sami about this principle" deep-link: ?q=...&ask=1).
  const autoAskRef = useRef(null);
  const autoAskSentRef = useRef(false);

  // Prefill from ?q= URL param (e.g. when arriving from the Laws FAQ
  // or the "Ask Sami about this principle" button on a judgment page).
  useEffect(() => {
    const q = searchParams.get("q");
    const ask = searchParams.get("ask");
    if (q && !input && messages.length === 0) {
      setInput(q);
      if (ask === "1") autoAskRef.current = q;
      const next = new URLSearchParams(searchParams);
      next.delete("q");
      next.delete("ask");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const t = useMemo(() => ({
    eyebrow: isAr ? "سامي — المستشار القانوني الذكي" : "Sami — AI Legal Consultant",
    title: isAr
      ? "اطرح سؤالك على سامي — مدعومًا بنصوص القانون الكويتي."
      : "Ask Sami — grounded in Kuwaiti statutes.",
    lead: isAr
      ? "سامي هو المستشار الذكي لمجموعة الدخيل، يستند إلى نصوص قوانين دولة الكويت التي رفعتها الإدارة، ويُعطي توجيهًا أوليًا. لا يُغني عن استشارة محامٍ مرخّص."
      : "Sami is the Aldakheel AI consultant. He references Kuwaiti law texts uploaded by the firm and provides initial guidance. He does not replace counsel from a licensed attorney.",
    placeholder: isAr ? "اكتب سؤالك هنا..." : "Type your question here…",
    send: isAr ? "إرسال" : "Send",
    thinking: isAr ? "سامي يفكّر..." : "Sami is thinking…",
    err: isAr ? "تعذّر الحصول على رد. حاول مرة أخرى." : "Could not get a reply. Try again.",
    library: isAr ? "مكتبة القوانين" : "Law Library",
    libraryEmpty: isAr ? "لم يتم رفع أي قانون بعد." : "No laws uploaded yet.",
    upload: isAr ? "رفع قانون" : "Upload law",
    upTitle: isAr ? "العنوان" : "Title",
    upLang: isAr ? "اللغة" : "Language",
    upFile: isAr ? "الملف (PDF أو TXT — حتى 100 م.ب.)" : "File (PDF or TXT — up to 100 MB)",
    uploadDo: isAr ? "رفع" : "Upload",
    uploaded: isAr ? "تم الرفع" : "Uploaded",
    delete: isAr ? "حذف" : "Delete",
    confirmDelete: isAr ? "حذف هذا القانون؟" : "Delete this law?",
    welcome: isAr
      ? "مرحبًا، أنا سامي — المستشار القانوني الذكي لمجموعة الدخيل. كيف يمكنني مساعدتك اليوم؟"
      : "Hello, I'm Sami — the Aldakheel AI legal consultant. How can I help you today?",
    publicMode: isAr ? "العامة" : "Public",
    staffMode: isAr ? "الموظفون" : "Staff",
    publicHint: isAr ? "إجابة موجزة للزوار" : "Brief overview for visitors",
    staffHint: isAr ? "بحث شامل ومفصّل" : "Full in-depth analysis",
    staffLockedTitle: isAr ? "وضع الموظفين يتطلب تسجيل الدخول" : "Staff mode requires sign-in",
    staffLockedBody: isAr
      ? "هذا الوضع متاح لمحامي وموظفي مجموعة الدخيل فقط. يُرجى تسجيل الدخول بحساب موظف."
      : "This mode is reserved for Aldakheel lawyers and staff. Please sign in with a staff account.",
    signInCta: isAr ? "تسجيل الدخول" : "Sign in",
    publicTagline: isAr
      ? "إجابة موجزة لكل زائر — للحصول على التحليل الكامل، يُرجى التواصل معنا."
      : "Concise overview for every visitor — for the full analysis, please contact us.",
    staffTagline: isAr
      ? "وضع المحترفين: تحليل شامل، اقتباسات كاملة، توصيات استراتيجية."
      : "Professional mode: in-depth analysis, full quotations, strategic recommendations.",
    historyButton: isAr ? "محادثاتي السابقة" : "My previous chats",
    historyTitle: isAr ? "محادثاتي مع سامي" : "My chats with Sami",
    historyEmpty: isAr ? "لا توجد محادثات سابقة بعد." : "No previous conversations yet.",
    historyLoading: isAr ? "جارٍ تحميل المحادثات…" : "Loading conversations…",
    historyResume: isAr ? "متابعة هذه المحادثة" : "Resume this conversation",
    historyTurns: (n) => isAr ? `${n} ${n === 1 ? "سؤال" : "أسئلة"}` : `${n} ${n === 1 ? "turn" : "turns"}`,
    historyClose: isAr ? "إغلاق" : "Close",
    historyNewSession: isAr ? "بدء محادثة جديدة" : "Start a new chat",
    copy: isAr ? "نسخ" : "Copy",
    copied: isAr ? "تم النسخ" : "Copied",
    copyFailed: isAr ? "تعذّر النسخ" : "Could not copy",
  }), [isAr]);

  // ---- Copy-to-clipboard helper -------------------------------------------------
  // Tracks which message index was just copied so we can flash a check icon.
  const [copiedIdx, setCopiedIdx] = useState(null);

  const copyMessage = useCallback(async (text, idx) => {
    if (!text) return;
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        ok = true;
      } else {
        // Fallback for older browsers / non-HTTPS preview hosts.
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.top = "-1000px";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        try {
          ok = document.execCommand("copy");
        } catch (_e) {
          ok = false;
        }
        document.body.removeChild(ta);
      }
    } catch (_err) {
      ok = false;
    }
    if (ok) {
      setCopiedIdx(idx);
      toast.success(t.copied);
      setTimeout(() => setCopiedIdx((curr) => (curr === idx ? null : curr)), 1600);
    } else {
      toast.error(t.copyFailed);
    }
  }, [t.copied, t.copyFailed]);

  const loadLaws = useCallback(() => {
    api.get("/laws").then((r) => setLaws(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadLaws();
    setMessages([{ role: "assistant", content: t.welcome }]);
  }, [isAr, tier, loadLaws, t.welcome]);

  // If user logs out while in staff mode, drop back to public
  useEffect(() => {
    if (tier === "staff" && !isStaffUser) setTier("public");
  }, [user, tier, isStaffUser]);

  useEffect(() => {
    // auto-scroll to bottom
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, busy]);

  const onSend = async (e, overrideText) => {
    e?.preventDefault();
    const text = (overrideText ?? input).trim();
    if (!text || busy) return;

    const next = [...messages, { role: "user", content: text }];
    // Insert an empty assistant placeholder we'll fill as tokens stream in.
    setMessages([...next, { role: "assistant", content: "", streaming: true }]);
    setInput("");
    setBusy(true);

    const body = {
      message: text,
      history: next.slice(0, -1).filter((m) => m.role !== "system"),
      language: lang,
      tier,
      session_id: _sessionIdRef.current,
      stream: true,
    };

    try {
      const res = await fetch(`${API_BASE}/consultant/chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
        body: JSON.stringify(body),
      });

      if (!res.ok || !res.body) {
        const status = res.status;
        let detail = "";
        try {
          const j = await res.json();
          detail = j?.detail || "";
        } catch (e) {
          // Some upstream proxies return non-JSON error bodies
          // (HTML, plain text). Falling back to the generic message
          // below is correct; we just log for visibility.
          log.debug("consult stream: non-JSON error body", e?.message);
        }
        let msg = t.err;
        if (status === 401) {
          msg = isAr ? "يلزم تسجيل الدخول للوضع المهني." : "Sign in for staff mode.";
        } else if (status === 403) {
          msg = isAr ? "هذا الوضع للموظفين فقط." : "Staff access only.";
        } else if (status === 500 || status === 502 || status === 503 || status === 504) {
          // Neutral public message — never disclose key / billing details.
          msg = isAr
            ? "الخدمة غير متاحة مؤقتاً. يُرجى المحاولة بعد قليل أو التواصل مع المكتب."
            : "Service temporarily unavailable. Please try again shortly or contact the firm.";
        } else if (typeof detail === "string" && detail) {
          msg = detail;
        }
        toast.error(msg);
        // Drop the empty placeholder
        setMessages((curr) => curr.filter((m) => !m.streaming));
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder("utf-8");
      let buffer = "";
      let live = "";
      let finalReply = null;

      // Read the SSE stream chunk by chunk and route each `data:` line.
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE messages are separated by a blank line.
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const lines = raw.split("\n");
          for (const line of lines) {
            if (!line.startsWith("data:")) continue;
            const json = line.slice(5).trim();
            if (!json) continue;
            try {
              const evt = JSON.parse(json);
              if (evt.event === "error") {
                toast.error(evt.detail || t.err);
              } else if (evt.event === "end") {
                finalReply = evt.reply || live;
              } else if (typeof evt.token === "string") {
                live += evt.token;
                // Update the trailing assistant message in-place.
                setMessages((curr) => {
                  if (!curr.length) return curr;
                  const out = curr.slice(0, -1);
                  out.push({ role: "assistant", content: live, streaming: true });
                  return out;
                });
              }
            } catch (e) {
              // SSE chunks can be split mid-frame; the next chunk will
              // re-deliver a parseable line. Ignoring is the documented
              // protocol behaviour, but we still log for visibility.
              log.debug("consult stream: malformed SSE frame", e?.message);
            }
          }
        }
      }

      // Stream finished — swap in the deterministic final reply (after
      // dialect scrubbing) and clear the streaming flag.
      const replyText = finalReply ?? live;
      setMessages((curr) => {
        if (!curr.length) return curr;
        const out = curr.slice(0, -1);
        out.push({ role: "assistant", content: replyText });
        return out;
      });
    } catch (err) {
      const detail = err?.message;
      toast.error(typeof detail === "string" ? detail : t.err);
      setMessages((curr) => curr.filter((m) => !m.streaming));
    } finally {
      setBusy(false);
    }
  };

  // Auto-send the seeded question once (from "Ask Sami about this principle").
  useEffect(() => {
    if (
      autoAskRef.current &&
      !autoAskSentRef.current &&
      !busy &&
      messages.length > 0
    ) {
      autoAskSentRef.current = true;
      const q = autoAskRef.current;
      autoAskRef.current = null;
      setInput("");
      onSend(null, q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, busy]);

  const onUpload = async (e) => {
    e.preventDefault();
    if (!upTitle.trim() || !upFile) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("title", upTitle.trim());
      fd.append("language", upLang);
      fd.append("file", upFile);
      await api.post("/admin/laws/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      toast.success(t.uploaded);
      setUpTitle("");
      setUpFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      loadLaws();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.err);
    } finally {
      setUploading(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await api.delete(`/admin/laws/${id}`);
      loadLaws();
    } catch {
      toast.error(t.err);
    }
  };

  const openHistory = async () => {
    setHistoryOpen(true);
    if (history.length > 0) return;
    setLoadingHistory(true);
    try {
      const { data } = await api.get("/consultant/my-history");
      setHistory(data.sessions || []);
    } catch {
      toast.error(t.err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const resumeSession = (session) => {
    const restored = [{ role: "assistant", content: t.welcome }];
    for (const turn of session.turns || []) {
      if (turn.user_message) restored.push({ role: "user", content: turn.user_message });
      if (turn.assistant_reply) restored.push({ role: "assistant", content: turn.assistant_reply });
    }
    setMessages(restored);
    sessionStorage.setItem("sami_session_id", session.session_id);
    _sessionIdRef.current = session.session_id;
    setHistoryOpen(false);
  };

  const startNewSession = () => {
    const id = `s-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem("sami_session_id", id);
    _sessionIdRef.current = id;
    setMessages([{ role: "assistant", content: t.welcome }]);
    setHistoryOpen(false);
  };

  // Mode: "chat" (default Sami chat) or "memo" (paid Lawyer Sami)
  const [mode, setMode] = useState("chat");

  return (
    <div data-testid="consultant-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
      <div className="grid lg:grid-cols-12 gap-12 mb-10">
        <div className="lg:col-span-6">
          <p className="eyebrow">{t.eyebrow}</p>
          <h1 className="font-display text-5xl lg:text-6xl mt-4 leading-[1.0] tracking-tight">
            {t.title}
          </h1>
        </div>
        <div className="lg:col-span-6 lg:pt-3">
          <p className="text-lg text-slate-600 leading-relaxed">{t.lead}</p>
        </div>
      </div>

      {/* Mode toggle: chat vs memo */}
      <div className="mb-6 flex flex-wrap items-center gap-2 border border-slate-200 bg-white p-1 w-fit" data-testid="consultant-mode-toggle">
        <button
          type="button"
          onClick={() => setMode("chat")}
          data-testid="mode-chat"
          className={`px-5 h-10 text-sm transition-colors ${
            mode === "chat" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          {isAr ? "استشارة سامي" : "Ask Sami"}
        </button>
        <button
          type="button"
          onClick={() => setMode("memo")}
          data-testid="mode-memo"
          className={`px-5 h-10 text-sm inline-flex items-center gap-2 transition-colors ${
            mode === "memo" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"
          }`}
        >
          <Sparkles className="w-3.5 h-3.5" />
          {isAr ? "سامي PRO" : "Sami PRO"}
          <span className="text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 border border-current">
            {isAr ? "احترافي" : "Pro"}
          </span>
        </button>
      </div>

      {mode === "memo" ? (
        <SamiMemoMode />
      ) : (
      <>

      {/* Tier toggle — staff button only visible to staff/admin users */}
      <div className="mb-8 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 border border-slate-200 bg-white p-1 w-fit">
          <button
            type="button"
            onClick={() => setTier("public")}
            data-testid="tier-public"
            className={`px-4 h-10 text-sm transition-colors ${
              tier === "public"
                ? "bg-slate-900 text-white"
                : "text-slate-700 hover:bg-slate-50"
            }`}
          >
            <div className="text-start">
              <div>{t.publicMode}</div>
              <div className={`text-[10px] uppercase tracking-[0.18em] ${tier === "public" ? "text-slate-300" : "text-slate-400"}`}>
                {t.publicHint}
              </div>
            </div>
          </button>
          {isStaffUser && (
            <button
              type="button"
              onClick={() => setTier("staff")}
              data-testid="tier-staff"
              className={`px-4 h-10 text-sm transition-colors ${
                tier === "staff"
                  ? "bg-slate-900 text-white"
                  : "text-slate-700 hover:bg-slate-50"
              }`}
            >
              <div className="text-start">
                <div>{t.staffMode}</div>
                <div className={`text-[10px] uppercase tracking-[0.18em] ${tier === "staff" ? "text-slate-300" : "text-slate-400"}`}>
                  {t.staffHint}
                </div>
              </div>
            </button>
          )}
        </div>

        {/* My previous chats — only for signed-in users */}
        {user && (
          <button
            type="button"
            onClick={openHistory}
            data-testid="open-my-history"
            className="inline-flex items-center gap-2 h-10 px-4 border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            {t.historyButton}
          </button>
        )}
      </div>

      <p className="text-sm text-slate-500 mb-6">
        {tier === "staff" ? t.staffTagline : t.publicTagline}
      </p>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* CHAT */}
        <div className="lg:col-span-8">
          <div className="border border-slate-200 bg-white flex flex-col h-[640px]">
            <div ref={scrollRef} className="flex-1 overflow-auto p-6 lg:p-8 space-y-6">
              {messages.map((m, i) => {
                // Hide an empty streaming placeholder — the dedicated
                // "thinking" indicator below already covers the wait.
                if (m.role === "assistant" && m.streaming && !m.content) {
                  return null;
                }
                return (
                <div
                  key={`msg-${i}-${m.role}`}
                  data-testid={`msg-${m.role}-${i}`}
                  className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse rtl:flex-row" : ""}`}
                >
                  <div
                    className={`shrink-0 w-9 h-9 grid place-items-center border ${
                      m.role === "user"
                        ? "bg-slate-900 text-white border-slate-900"
                        : "bg-white text-slate-700 border-slate-200"
                    }`}
                  >
                    {m.role === "user" ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4" />}
                  </div>
                  <div
                    dir="auto"
                    className={`max-w-[80%] text-[15px] leading-[1.85] whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-slate-900 text-white px-4 py-3"
                        : "bg-slate-50 text-slate-900 px-4 py-3 border border-slate-200"
                    }`}
                  >
                    {m.content}
                    {m.streaming && (
                      <span
                        aria-hidden
                        className="inline-block w-[2px] h-[1em] align-[-2px] ms-1 bg-slate-700 animate-pulse"
                      />
                    )}
                    {/* Copy button — only on completed assistant replies
                        with actual text (not greetings stripped to empty
                        and not while streaming). */}
                    {/* Footer actions: Book-with-Sami-context + Copy.
                        Sami's "احجز محامي" CTA forwards the user's most
                        recent question into the WhatsApp pre-fill so the
                        firm receives the context without re-asking. */}
                    {m.role === "assistant" && !m.streaming && m.content && m.content.trim() && (
                      <div className="mt-3 pt-2 border-t border-slate-200/70 flex flex-wrap items-center justify-between gap-2">
                        <a
                          href={buildSamiWaLink(messages, i, isAr)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`sami-book-${i}`}
                          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-sm transition-colors"
                          style={{ background: "var(--wa)", color: "#fff" }}
                        >
                          <MessageCircle className="w-3.5 h-3.5" />
                          {isAr ? "احجز محامي بهذا السؤال" : "Book a lawyer with this question"}
                        </a>
                        <button
                          type="button"
                          onClick={() => copyMessage(m.content, i)}
                          data-testid={`copy-assistant-${i}`}
                          aria-label={t.copy}
                          title={t.copy}
                          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] text-slate-500 hover:text-slate-900 transition-colors px-2 py-1"
                        >
                          {copiedIdx === i ? (
                            <>
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                              <span className="text-emerald-700">{t.copied}</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-3.5 h-3.5" />
                              <span>{t.copy}</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                );
              })}
              {busy && !messages.some((m) => m.streaming && m.content) && (
                <div className="flex gap-3">
                  <div className="shrink-0 w-9 h-9 grid place-items-center border bg-white border-slate-200">
                    <Bot className="w-4 h-4" />
                  </div>
                  <div className="bg-slate-50 border border-slate-200 px-4 py-3 text-slate-500 inline-flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> {t.thinking}
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={onSend} className="border-t border-slate-200" data-testid="consultant-form">
              <div
                className="px-3 pt-3 pb-2 text-[11px] text-slate-500 leading-relaxed border-b border-slate-100"
                data-testid="consultant-disclaimer"
              >
                {isAr ? (
                  <>
                    الردود الصادرة من سامي هي للمساعدة الأولية فقط ولا تُعدّ رأياً قانونياً نهائياً ولا تنشئ علاقة محاماة.{" "}
                    <a href="/terms" target="_blank" rel="noreferrer" className="underline underline-offset-2 text-slate-700 hover:text-slate-900">الشروط والأحكام</a>
                  </>
                ) : (
                  <>
                    Sami's responses are preliminary assistance only — not a final legal opinion and they do not create an attorney-client relationship.{" "}
                    <a href="/terms" target="_blank" rel="noreferrer" className="underline underline-offset-2 text-slate-700 hover:text-slate-900">Terms</a>
                  </>
                )}
              </div>
              <div className="p-3 flex gap-2">
                <Input
                  dir="auto"
                  placeholder={t.placeholder}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={busy}
                  data-testid="consultant-input"
                  className="rounded-sm h-11"
                />
                <Button
                  type="submit"
                  disabled={busy || !input.trim()}
                  data-testid="consultant-send"
                  className="rounded-sm h-11 px-5 bg-slate-900 hover:bg-slate-800 text-white"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  <span className="ms-2 hidden sm:inline">{t.send}</span>
                </Button>
              </div>
            </form>
          </div>
        </div>

        {/* LIBRARY */}
        <aside className="lg:col-span-4 space-y-6">
          <div className="border border-slate-200 bg-white p-6">
            <div className="flex items-center gap-2 mb-4">
              <ScrollText className="w-4 h-4 text-slate-500" />
              <p className="eyebrow">{t.library}</p>
            </div>
            {laws.length === 0 ? (
              <p className="text-sm text-slate-500">{t.libraryEmpty}</p>
            ) : (
              <ul className="space-y-3 max-h-[280px] overflow-auto">
                {laws.map((l) => (
                  <li
                    key={l.id}
                    data-testid={`law-row-${l.id}`}
                    className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{l.title}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {l.filename} · {formatBytes(l.char_count)}
                      </p>
                    </div>
                    {isAdmin && (
                      <button
                        type="button"
                        onClick={() => onDelete(l.id)}
                        data-testid={`law-delete-${l.id}`}
                        className="text-slate-400 hover:text-red-600 transition-colors shrink-0"
                        aria-label={t.delete}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {isAdmin && (
            <form
              onSubmit={onUpload}
              className="border border-slate-200 bg-white p-6 space-y-4"
              data-testid="law-upload-form"
            >
              <div className="flex items-center gap-2">
                <Upload className="w-4 h-4 text-slate-500" />
                <p className="eyebrow">{t.upload}</p>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.upTitle}</Label>
                <Input
                  dir="auto"
                  required
                  value={upTitle}
                  onChange={(e) => setUpTitle(e.target.value)}
                  data-testid="law-title"
                  className="mt-2 rounded-sm h-11"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.upLang}</Label>
                <Select value={upLang} onValueChange={setUpLang}>
                  <SelectTrigger data-testid="law-language" className="mt-2 rounded-sm h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ar">{isAr ? "العربية" : "Arabic"}</SelectItem>
                    <SelectItem value="en">{isAr ? "الإنجليزية" : "English"}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-slate-500">{t.upFile}</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  required
                  accept=".pdf,.txt,.md"
                  onChange={(e) => setUpFile(e.target.files?.[0] || null)}
                  data-testid="law-file"
                  className="mt-2 block w-full text-sm file:mr-4 file:py-2 file:px-4 file:border file:border-slate-300 file:bg-white file:text-slate-700 hover:file:border-slate-900"
                />
              </div>
              <Button
                type="submit"
                disabled={uploading || !upFile}
                data-testid="law-upload-submit"
                className="rounded-sm h-11 w-full bg-slate-900 hover:bg-slate-800 text-white"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : t.uploadDo}
              </Button>
            </form>
          )}
        </aside>
      </div>

      {/* My-history slide-over panel */}
      {historyOpen && (
        <div
          className="fixed inset-0 z-50 flex"
          data-testid="my-history-panel"
          onClick={() => setHistoryOpen(false)}
        >
          <div className="flex-1 bg-slate-900/40" />
          <div
            className="w-full max-w-md h-full bg-white border-l border-slate-200 flex flex-col"
            onClick={(e) => e.stopPropagation()}
            dir={isAr ? "rtl" : "ltr"}
          >
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <div>
                <p className="eyebrow">{t.eyebrow}</p>
                <h2 className="font-display text-xl mt-1">{t.historyTitle}</h2>
              </div>
              <button
                type="button"
                onClick={() => setHistoryOpen(false)}
                aria-label={t.historyClose}
                data-testid="close-my-history"
                className="text-slate-500 hover:text-slate-900 p-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5 border-b border-slate-200">
              <button
                type="button"
                onClick={startNewSession}
                data-testid="start-new-session"
                className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 bg-slate-900 hover:bg-slate-800 text-white text-sm transition-colors"
              >
                {t.historyNewSession}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {loadingHistory && (
                <div className="text-sm text-slate-500 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t.historyLoading}
                </div>
              )}
              {!loadingHistory && history.length === 0 && (
                <div className="text-sm text-slate-500">{t.historyEmpty}</div>
              )}
              {!loadingHistory && history.map((s) => {
                const firstQ = s.turns?.[0]?.user_message || "";
                return (
                  <button
                    key={s.session_id}
                    type="button"
                    onClick={() => resumeSession(s)}
                    data-testid={`history-session-${s.session_id}`}
                    className="w-full text-start border border-slate-200 hover:border-slate-400 hover:bg-slate-50 p-4 transition-colors"
                  >
                    <p className="text-xs text-slate-500 mb-1">
                      {new Date(s.last_at).toLocaleString(isAr ? "ar" : "en")}
                      {" · "}
                      {t.historyTurns(s.turns?.length || 0)}
                    </p>
                    <p className="text-sm text-slate-900 line-clamp-2">
                      {firstQ}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
